import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useMe } from "../hooks/useMe";
import Sidebar from "../components/Sidebar";
import "../styles/profile.scss";
import "../styles/list.scss";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import API from "../APIs/API";
import WARD_DATA from "../data/wardData";
import AIRPORTS from "../data/airports";
import REGION_DATA from "../data/regionData";
import OSTA_RATES from "../data/ostaRatesLocal";

const TRAVEL_TYPES = ["Domestic", "Overseas"];
const MODE_OF_TRAVEL = ["Sea", "Land", "Air"];

// ─── Cascading location helpers ─────────────────────────────────────────────
const getProvinces = () => Object.keys(WARD_DATA).sort();
const getDistricts = (province) => province ? Object.keys(WARD_DATA[province] || {}).sort() : [];
const getLLGs = (province, district) =>
  province && district ? Object.keys((WARD_DATA[province] || {})[district] || {}).sort() : [];
const getWards = (province, district, llg) =>
  province && district && llg ? (((WARD_DATA[province] || {})[district] || {})[llg] || []).sort() : [];

// ─── LocationPicker sub-component ───────────────────────────────────────────
const LocationPicker = ({ label, prefix, values, onChange }) => {
  const provinces = getProvinces();
  const districts = getDistricts(values.province);
  const llgs = getLLGs(values.province, values.district);
  const wards = getWards(values.province, values.district, values.llg);

  const handleChange = (field, value) => {
    const reset = {};
    if (field === "province") { reset.district = ""; reset.llg = ""; reset.ward = ""; }
    if (field === "district") { reset.llg = ""; reset.ward = ""; }
    if (field === "llg")      { reset.ward = ""; }
    onChange({ ...values, [field]: value, ...reset });
  };

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "12px 16px", marginTop: 8 }}>
      <p style={{ fontWeight: 600, marginBottom: 10, color: "#374151", fontSize: 14 }}>{label}</p>
      <div className="row">
        <div className="col-md-3">
          <label className="form-label">Province</label>
          <TextField select fullWidth size="small" value={values.province}
            onChange={(e) => handleChange("province", e.target.value)}>
            <MenuItem value="">— Select —</MenuItem>
            {provinces.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
        </div>
        <div className="col-md-3">
          <label className="form-label">District</label>
          <TextField select fullWidth size="small" value={values.district}
            disabled={!values.province}
            onChange={(e) => handleChange("district", e.target.value)}>
            <MenuItem value="">— Select —</MenuItem>
            {districts.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </TextField>
        </div>
        <div className="col-md-3">
          <label className="form-label">LLG</label>
          <TextField select fullWidth size="small" value={values.llg}
            disabled={!values.district}
            onChange={(e) => handleChange("llg", e.target.value)}>
            <MenuItem value="">— Select —</MenuItem>
            {llgs.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
          </TextField>
        </div>
        <div className="col-md-3">
          <label className="form-label">Ward</label>
          <TextField select fullWidth size="small" value={values.ward}
            disabled={!values.llg}
            onChange={(e) => handleChange("ward", e.target.value)}>
            <MenuItem value="">— Select —</MenuItem>
            {wards.map(w => <MenuItem key={w} value={w}>{w}</MenuItem>)}
          </TextField>
        </div>
        <div className="col-md-4 mt-2">
          <label className="form-label">Town</label>
          <input className="form-control" type="text" value={values.town}
            onChange={(e) => onChange({ ...values, town: e.target.value })}
            placeholder="Enter town name" />
        </div>
      </div>
    </div>
  );
};

// ─── Empty location object ───────────────────────────────────────────────────
const emptyLocation = () => ({ province: "", district: "", llg: "", ward: "", town: "" });

const INITIAL_FORM = {
  travel_type: "",
  start_date: "",
  end_date: "",
  number_of_days: 0,
  resumption_day: "",
  has_supporting_documents: false,
  cash_advance_amount: "",
  cheque_number: "",
  ge_number: "",
  date_issued: "",
  supporting_files: [],
  accommodation_arranged: false,
  accommodation_files: [],
  purpose: "",
  mobile_numbers: [""],
  email_addresses: [""],
  // Domestic travel details
  mode_of_travel: "",
  from_location: emptyLocation(),
  to_location: emptyLocation(),
  from_airport: "",
  to_airport: "",
  travel_legs: [],
  // Overseas
  region: "",
  sub_region: "",
  country: "",
  state: "",
  city: "",
  currency: "",
  per_diem_usd: "",
  beyond_21_days_usd: "",
  departure_datetime: "",
  arrival_datetime: "",
};

const DutyTravelApply = ({ type }) => {
  const { admin_location_id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { permissions } = useMe();
  const [form, setForm] = useState(INITIAL_FORM);

  // Full offline states/cities dataset: { country: { state: [cities] } }
  const { data: statesCities = {} } = useQuery(
    ["states_cities"],
    async () => {
      try {
        const r = await fetch("/data/statesCities.json");
        if (!r.ok) return {};
        return await r.json();
      } catch { return {}; }
    },
    { staleTime: Infinity, cacheTime: Infinity }
  );

  const pageUserType = type === "Teacher" ? "teachers" : "non-teachers";
  const backUrl = `/${pageUserType}/${admin_location_id}/duty-travel`;

  const { data: ostaRates = [] } = useQuery(
    ["osta_rates"],
    async () => { const d = await new API().getOstaRates(); return Array.isArray(d) ? d : []; },
    { staleTime: 60000 }
  );

  const submitMutation = useMutation(
    (payload) => new API().createDutyTravel(payload),
    {
      onSuccess: (data) => {
        if (!data) { toast.error("Failed to submit."); return; }
        queryClient.invalidateQueries(["duty_travel"]);
        toast.success("Duty travel application submitted.");
        navigate(backUrl);
      },
      onError: () => toast.error("Failed to submit."),
    }
  );

  const availableRegions = Object.keys(REGION_DATA).sort();
  const subRegionsForRegion = form.region ? Object.keys(REGION_DATA[form.region] || {}).sort() : [];
  const countriesForSubRegion = form.region && form.sub_region
    ? ((REGION_DATA[form.region] || {})[form.sub_region] || [])
    : [];

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  // Resolve country key in the dataset (names may differ slightly from the xlsx)
  const resolveCountryKey = (country) => {
    if (!country) return null;
    if (statesCities[country]) return country;
    const lower = country.toLowerCase();
    const keys = Object.keys(statesCities);
    return keys.find(k => k.toLowerCase() === lower)
      || keys.find(k => k.toLowerCase().includes(lower) || lower.includes(k.toLowerCase()))
      || null;
  };

  const countryKey = resolveCountryKey(form.country);
  const stateOptions = countryKey ? Object.keys(statesCities[countryKey] || {}).sort() : [];
  const cityOptions = countryKey && form.state
    ? [...((statesCities[countryKey] || {})[form.state] || [])].sort()
    : [];

  const handleRegionChange = (e) => {
    setForm(prev => ({ ...prev, region: e.target.value, sub_region: "", country: "", state: "", city: "", currency: "", per_diem_usd: "" }));
  };

  const handleSubRegionChange = (e) => {
    setForm(prev => ({ ...prev, sub_region: e.target.value, country: "", state: "", city: "", currency: "", per_diem_usd: "" }));
  };

  const findOstaRate = (country) => {
    if (!country) return null;
    const lower = country.toLowerCase();
    return OSTA_RATES.find(r => r.country.toLowerCase() === lower)
      || OSTA_RATES.find(r => lower.includes(r.country.toLowerCase()) || r.country.toLowerCase().includes(lower))
      || null;
  };

  const handleCountryChange = (e) => {
    const country = e.target.value;
    const rate = findOstaRate(country);
    setForm(prev => ({
      ...prev,
      country,
      state: "",
      city: "",
      currency: rate ? rate.currency : "",
      per_diem_usd: rate ? rate.per_diem_usd : "",
      beyond_21_days_usd: rate ? rate.beyond_21_days_usd : "",
    }));
  };

  // Total per diem: rate × days; days beyond 21 are paid at the 60% rate
  const perDiemTotal = (() => {
    const rate = Number(form.per_diem_usd);
    const days = Number(form.number_of_days);
    if (!rate || !days) return "";
    const beyondRate = Number(form.beyond_21_days_usd) || Math.round(rate * 0.6);
    const total = days <= 21
      ? rate * days
      : rate * 21 + beyondRate * (days - 21);
    return total.toFixed(2);
  })();

  const handleStateChange = (e) => {
    setForm(prev => ({ ...prev, state: e.target.value, city: "" }));
  };

  const handleModeChange = (e) => {
    setForm(prev => ({
      ...prev,
      mode_of_travel: e.target.value,
      from_location: emptyLocation(),
      to_location: emptyLocation(),
      from_airport: "",
      to_airport: "",
    }));
  };

  const formatLocation = (loc) => {
    const parts = [loc.province, loc.district, loc.llg, loc.ward].filter(Boolean);
    if (loc.town) parts.push(loc.town);
    return parts.join(" › ") || "—";
  };

  const handleAddLeg = () => {
    if (!form.mode_of_travel) { toast.warning("Select a Mode of Travel first."); return; }
    if (form.mode_of_travel === "Air") {
      if (!form.from_airport || !form.to_airport) { toast.warning("Select both From and To airports."); return; }
    } else {
      if (!form.from_location.province || !form.to_location.province) {
        toast.warning("Select at least a Province for both From and To."); return;
      }
    }
    const leg = {
      mode: form.mode_of_travel,
      from: form.mode_of_travel === "Air" ? form.from_airport : formatLocation(form.from_location),
      to:   form.mode_of_travel === "Air" ? form.to_airport   : formatLocation(form.to_location),
    };
    setForm(prev => ({
      ...prev,
      travel_legs: [...prev.travel_legs, leg],
      mode_of_travel: "",
      from_location: emptyLocation(),
      to_location: emptyLocation(),
      from_airport: "",
      to_airport: "",
    }));
  };

  const recalcDays = (start, end) => {
    if (!start || !end) return;
    // Use date portion only for day calculations
    const startDate = dayjs(start.substring(0, 10));
    const endDate   = dayjs(end.substring(0, 10));
    const days = endDate.diff(startDate, "day") + 1;
    // Resumption: day after return; if return falls on Fri/Sat/Sun → the Monday
    const returnDow = endDate.day(); // 0=Sun, 5=Fri, 6=Sat
    let resumption;
    if (returnDow === 5)      resumption = endDate.add(3, "day"); // Fri → Mon
    else if (returnDow === 6) resumption = endDate.add(2, "day"); // Sat → Mon
    else if (returnDow === 0) resumption = endDate.add(1, "day"); // Sun → Mon
    else                      resumption = endDate.add(1, "day");
    setForm(prev => ({
      ...prev,
      number_of_days: days > 0 ? days : 0,
      resumption_day: resumption.format("YYYY-MM-DD"),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.travel_type)    { toast.warning("Select a travel type."); return; }
    if (!form.start_date)     { toast.warning("Start date is required."); return; }
    if (!form.end_date)       { toast.warning("End date is required."); return; }
    if (!form.purpose.trim()) { toast.warning("Purpose/Description is required."); return; }

    const travelTypeLower = form.travel_type.toLowerCase();

    const domesticDetails = travelTypeLower === "domestic" ? {
      travel_legs: form.travel_legs.length > 0 ? form.travel_legs : undefined,
    } : {};

    submitMutation.mutate({
      admin_location_id: Number(admin_location_id),
      travel_type: travelTypeLower,
      start_date: form.start_date,
      end_date: form.end_date,
      has_supporting_documents: form.has_supporting_documents,
      cash_advance_amount: form.cash_advance_amount ? Number(form.cash_advance_amount) : undefined,
      cheque_number: form.cheque_number || undefined,
      ge_number: form.ge_number || undefined,
      accommodation_arranged: form.accommodation_arranged,
      accommodation_files: [],
      purpose: form.purpose,
      mobile_numbers: form.mobile_numbers.filter(Boolean),
      email_addresses: form.email_addresses.filter(Boolean),
      ...domesticDetails,
      region: travelTypeLower === "overseas" ? form.region : undefined,
      sub_region: travelTypeLower === "overseas" ? form.sub_region : undefined,
      country: travelTypeLower === "overseas" ? form.country : undefined,
      state: travelTypeLower === "overseas" ? form.state : undefined,
      city: travelTypeLower === "overseas" ? form.city : undefined,
    });
  };

  return (
    <div className="wrapper">
      <div className="sidebar"><Sidebar /></div>
      <div className="content">
        <div className="profile">
          <div className="profileContainer">
            <div className="profileSection">

              <Link to={backUrl} className="backButtonSection">
                <KeyboardBackspaceIcon className="backButton" />
              </Link>
              <br /><br />

              <form onSubmit={handleSubmit}>
                <div className="container">

                  <div className="row">
                    <div className="col">
                      <h1>Apply for Duty Travel</h1>
                    </div>
                  </div>
                  <hr />

                  {/* 1. Travel Type */}
                  <div className="row mt-3">
                    <div className="col-md-4">
                      <label className="form-label"><b>1. Travel Type *</b></label>
                      <TextField select fullWidth size="small" value={form.travel_type} onChange={set("travel_type")}>
                        {TRAVEL_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </TextField>
                    </div>
                  </div>

                  {/* 2 & 3. Dates */}
                  <div className="row mt-3">
                    <div className="col-md-3">
                      <label className="form-label"><b>2. Departure Date &amp; Time *</b></label>
                      <input
                        className="form-control" type="datetime-local"
                        value={form.start_date}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm(prev => ({ ...prev, start_date: v }));
                          recalcDays(v, form.end_date);
                        }}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label"><b>3. Return Date &amp; Time *</b></label>
                      <input
                        className="form-control" type="datetime-local"
                        value={form.end_date}
                        min={form.start_date}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm(prev => ({ ...prev, end_date: v }));
                          recalcDays(form.start_date, v);
                        }}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label"><b>4. Number of Days</b></label>
                      <input className="form-control" type="text" value={form.number_of_days} disabled />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label"><b>5. Resumption Day</b></label>
                      <input className="form-control" type="text" value={form.resumption_day} disabled />
                    </div>
                  </div>

                  {/* 6. Supporting Documents */}
                  <div className="row mt-3">
                    <div className="col-md-4">
                      <label className="form-label"><b>6. Supporting Documents?</b></label>
                      <TextField select fullWidth size="small"
                        value={form.has_supporting_documents ? "Yes" : "No"}
                        onChange={(e) => setForm(prev => ({ ...prev, has_supporting_documents: e.target.value === "Yes" }))}>
                        <MenuItem value="No">No</MenuItem>
                        <MenuItem value="Yes">Yes</MenuItem>
                      </TextField>
                    </div>
                    {form.has_supporting_documents && (
                      <div className="col-md-8">
                        <label className="form-label"><b>Upload Supporting Documents</b></label>
                        <input type="file" className="form-control" multiple accept=".jpg,.jpeg,.png,.tiff,.pdf"
                          onChange={(e) => setForm(prev => ({ ...prev, supporting_files: Array.from(e.target.files) }))} />
                        <small style={{ color: "#6b7280" }}>JPEG, PNG, TIFF, PDF</small>
                      </div>
                    )}
                  </div>

                  {/* 7. Cash Advance */}
                  <div className="row mt-3">
                    <div className="col-md-3">
                      <label className="form-label"><b>7. Cash Advance Amount (PGK)</b></label>
                      <input className="form-control" type="number" min="0" step="0.01" value={form.cash_advance_amount} onChange={set("cash_advance_amount")} placeholder="0.00" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label"><b>Cheque Number</b></label>
                      <input className="form-control" type="text" value={form.cheque_number} onChange={set("cheque_number")} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label"><b>GE Number</b></label>
                      <input className="form-control" type="text" value={form.ge_number} onChange={set("ge_number")} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label"><b>Date Issued</b></label>
                      <input className="form-control" type="date" value={form.date_issued} onChange={set("date_issued")} />
                    </div>
                  </div>

                  {/* 8. Accommodation */}
                  <div className="row mt-3">
                    <div className="col-md-4">
                      <label className="form-label"><b>8. Accommodation Arranged?</b></label>
                      <TextField select fullWidth size="small"
                        value={form.accommodation_arranged ? "Yes" : "No"}
                        onChange={(e) => setForm(prev => ({ ...prev, accommodation_arranged: e.target.value === "Yes" }))}>
                        <MenuItem value="No">No</MenuItem>
                        <MenuItem value="Yes">Yes</MenuItem>
                      </TextField>
                    </div>
                    {form.accommodation_arranged && (
                      <div className="col-md-8">
                        <label className="form-label"><b>Upload Accommodation Documents</b></label>
                        <input type="file" className="form-control" multiple accept=".jpg,.jpeg,.png,.tiff,.pdf"
                          onChange={(e) => setForm(prev => ({ ...prev, accommodation_files: Array.from(e.target.files) }))} />
                        <small style={{ color: "#6b7280" }}>JPEG, PNG, TIFF, PDF</small>
                      </div>
                    )}
                  </div>

                  {/* 9. Purpose */}
                  <div className="row mt-3">
                    <div className="col-md-12">
                      <label className="form-label"><b>9. Purpose / Description *</b></label>
                      <textarea className="form-control" rows={3} value={form.purpose} onChange={set("purpose")} placeholder="Describe the purpose of the duty travel..." />
                    </div>
                  </div>

                  {/* 10 & 11. Contact */}
                  <div className="row mt-3">
                    <div className="col-md-6">
                      <label className="form-label"><b>10. Mobile Number(s)</b></label>
                      {form.mobile_numbers.map((num, i) => (
                        <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                          <input className="form-control" type="tel" value={num} placeholder="+675 XXXX XXXX"
                            onChange={(e) => { const a = [...form.mobile_numbers]; a[i] = e.target.value; setForm(prev => ({ ...prev, mobile_numbers: a })); }} />
                          {i === form.mobile_numbers.length - 1 && (
                            <button type="button" className="btn btn-outline-secondary btn-sm"
                              onClick={() => setForm(prev => ({ ...prev, mobile_numbers: [...prev.mobile_numbers, ""] }))}>+</button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label"><b>11. Email Address(es)</b></label>
                      {form.email_addresses.map((email, i) => (
                        <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                          <input className="form-control" type="email" value={email} placeholder="email@example.com"
                            onChange={(e) => { const a = [...form.email_addresses]; a[i] = e.target.value; setForm(prev => ({ ...prev, email_addresses: a })); }} />
                          {i === form.email_addresses.length - 1 && (
                            <button type="button" className="btn btn-outline-secondary btn-sm"
                              onClick={() => setForm(prev => ({ ...prev, email_addresses: [...prev.email_addresses, ""] }))}>+</button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 12. Travel Details */}
                  <div className="row mt-3">
                    <div className="col-md-12">
                      <label className="form-label">
                        <b>12. {form.travel_type === "Domestic" ? "Travel Details" : "Travel Destination"}</b>
                      </label>
                    </div>

                    {/* ── DOMESTIC ── */}
                    {form.travel_type === "Domestic" && (
                      <div className="col-md-12">

                        {/* ── Entry box ── */}
                        <div style={{ border: "1px solid #d1d5db", borderRadius: 8, padding: "16px 20px", background: "#f9fafb" }}>

                          {/* Mode of Travel */}
                          <div className="row">
                            <div className="col-md-4">
                              <label className="form-label">Mode of Travel</label>
                              <TextField select fullWidth size="small" value={form.mode_of_travel} onChange={handleModeChange}>
                                <MenuItem value="">— Select —</MenuItem>
                                {MODE_OF_TRAVEL.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                              </TextField>
                            </div>
                          </div>

                          {/* Sea / Land — cascading From & To pickers */}
                          {(form.mode_of_travel === "Sea" || form.mode_of_travel === "Land") && (
                            <>
                              <LocationPicker
                                label="From"
                                values={form.from_location}
                                onChange={(v) => setForm(prev => ({ ...prev, from_location: v }))}
                              />
                              <LocationPicker
                                label="To"
                                values={form.to_location}
                                onChange={(v) => setForm(prev => ({ ...prev, to_location: v }))}
                              />
                            </>
                          )}

                          {/* Air — airport dropdowns */}
                          {form.mode_of_travel === "Air" && (
                            <div className="row mt-2">
                              <div className="col-md-6">
                                <label className="form-label">From (Airport / Airstrip)</label>
                                <TextField select fullWidth size="small" value={form.from_airport}
                                  onChange={set("from_airport")}>
                                  <MenuItem value="">— Select airport —</MenuItem>
                                  {AIRPORTS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                                </TextField>
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">To (Airport / Airstrip)</label>
                                <TextField select fullWidth size="small" value={form.to_airport}
                                  onChange={set("to_airport")}>
                                  <MenuItem value="">— Select airport —</MenuItem>
                                  {AIRPORTS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                                </TextField>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* ── + button ── */}
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                          <button type="button" className="btn btn-outline-secondary btn-sm"
                            onClick={handleAddLeg}
                            title="Add this travel leg to the list">
                            +
                          </button>
                        </div>

                        {/* ── Travel legs table ── */}
                        {form.travel_legs.length > 0 && (
                          <table className="table table-bordered table-sm mt-2" style={{ fontSize: 13 }}>
                            <thead style={{ background: "#f3f4f6" }}>
                              <tr>
                                <th style={{ width: 90 }}>Mode</th>
                                <th>From</th>
                                <th>To</th>
                                <th style={{ width: 40 }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {form.travel_legs.map((leg, i) => (
                                <tr key={i}>
                                  <td>{leg.mode}</td>
                                  <td>{leg.from}</td>
                                  <td>{leg.to}</td>
                                  <td style={{ textAlign: "center" }}>
                                    <button type="button" className="btn btn-link btn-sm p-0"
                                      style={{ color: "#dc2626", lineHeight: 1 }}
                                      onClick={() => setForm(prev => ({ ...prev, travel_legs: prev.travel_legs.filter((_, j) => j !== i) }))}>
                                      ×
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}

                    {/* ── OVERSEAS ── */}
                    {form.travel_type === "Overseas" && (
                      <>
                        <div className="col-md-3">
                          <label className="form-label">Region</label>
                          <TextField select fullWidth size="small" value={form.region} onChange={handleRegionChange}>
                            <MenuItem value="">— Select —</MenuItem>
                            {availableRegions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                          </TextField>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Sub-region</label>
                          <TextField select fullWidth size="small" value={form.sub_region}
                            disabled={!form.region} onChange={handleSubRegionChange}>
                            <MenuItem value="">— Select —</MenuItem>
                            {subRegionsForRegion.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                          </TextField>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Country</label>
                          <TextField select fullWidth size="small" value={form.country}
                            disabled={!form.sub_region} onChange={handleCountryChange}>
                            <MenuItem value="">— Select country —</MenuItem>
                            {countriesForSubRegion.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                          </TextField>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">State</label>
                          <TextField select fullWidth size="small" value={form.state}
                            disabled={!form.country || stateOptions.length === 0} onChange={handleStateChange}>
                            <MenuItem value="">— Select —</MenuItem>
                            {stateOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                          </TextField>
                        </div>
                        <div className="col-md-3 mt-3">
                          <label className="form-label">City</label>
                          <TextField select fullWidth size="small" value={form.city}
                            disabled={!form.state || cityOptions.length === 0} onChange={set("city")}>
                            <MenuItem value="">— Select —</MenuItem>
                            {cityOptions.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                          </TextField>
                        </div>
                        <div className="col-md-3 mt-3">
                          <label className="form-label">Currency</label>
                          <input className="form-control" type="text" value={form.currency} disabled placeholder="Auto-filled" />
                        </div>
                        <div className="col-md-3 mt-3">
                          <label className="form-label">Per Diem (USD)</label>
                          <input className="form-control" type="text" value={perDiemTotal ? `$${perDiemTotal}` : ""} disabled placeholder="Auto-calculated" />
                          {form.per_diem_usd && form.number_of_days > 0 && (
                            <small style={{ color: form.number_of_days > 21 ? "#dc2626" : "#6b7280" }}>
                              {form.number_of_days > 21
                                ? `21 days × $${form.per_diem_usd} + ${form.number_of_days - 21} days × $${form.beyond_21_days_usd || Math.round(Number(form.per_diem_usd) * 0.6)} (60%)`
                                : `${form.number_of_days} days × $${form.per_diem_usd}`}
                            </small>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  <hr className="mt-4" />

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                    <Link to={backUrl}>
                      <button type="button" className="btn btn-secondary">Cancel</button>
                    </Link>
                    <button type="submit" className="btn btn-primary" disabled={submitMutation.isLoading}>
                      {submitMutation.isLoading ? "Submitting…" : "Submit Duty Travel Request"}
                    </button>
                  </div>

                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DutyTravelApply;
